import React, { useEffect, useState } from "react";
import { AddOnsFilter } from "./SupplementsForDishesPage";
import { Button, Form, OverlayTrigger, Table, Tooltip } from "react-bootstrap";
import {
    BadgeAndButtonForArchive,
    Pagination,
    RenderEmptyList,
    RenderNamesCell,
    Spinner,
    WithRoleAccess,
} from "@services/ui-components";
import emptyImage from "../../../images/additives-empty-page.png";
import { useTranslation } from "react-i18next";
import { ApolloError, useReactiveVar } from "@apollo/client";
import ErrorService from "../../../services/ErrorService";
import {
    useDeleteMenuSupplementMutation,
    useGetDishSupplementsQuery,
    useUpdateMenuSupplementMutation,
} from "@me-team/host/main/dishSupplements/graphql/dishSupplements.hooks";
import { currentCompanyIdVar } from "@me-team/host/main/globalVar/state";
import { PartialDishSupplement } from "../../../interfaces/meMenuInterfaces/interfaces";
import ToastComponent from "../../ui-components/ToastComponent/ToastComponent";
import i18next from "i18next";
import { useToast } from "../../../hooks/useToast";
import { ErrorModalComponent } from "@me-pos/error-modal";
import ConfirmDeleteModal from "./modals/ConfirmDeleteModal/ConfirmDeleteModal";
import { GroupOverrides, makeInputForUpdateGroup } from "./utils/makeInputForUpdateGroup";
import { cloneNameToDisplay } from "../utils/cloneNameUtils";
import useScrollToTopOnChange from "../../../hooks/useScrollToTopOnChange";
import { useMenuLanguagesData } from "../utils/hooks/useMenuLanguagesData";
import { buildTranslationTextVm } from "../utils/translationVersions/buildTranslationTextVm";
import TranslationMissingDot from "../components/LanguageVersionSelect/TranslationMissingDot";
import { CompanyAccess } from "@me-team/host/main/graphql/types";
import { useRoleAccess } from "@me-team/host/src/utils/useRoleAccess";
import { useParams } from "react-router-dom";

interface SupplementsForDishesTableProps {
    versionLocale: string | null;
    filters: AddOnsFilter;
    onCreateGroup: () => void;
    onEditGroup: (group: PartialDishSupplement) => void;
    onRestoreGroup: (group: PartialDishSupplement) => void;
    onRefetchReady?: React.Dispatch<React.SetStateAction<(() => Promise<unknown>) | null>>;
    tooltipResetKey?: number;
    resetTooltips?: () => void;
    canCreateOptionsAndSupplements?: boolean;
    canEditOptionsAndSupplements?: boolean;
    canDeleteRestoreOptionsAndSupplements?: boolean;
}

const ITEMS_PER_PAGE = 20;

const SupplementsForDishesTable: React.FC<SupplementsForDishesTableProps> = ({
    versionLocale,
    filters,
    onCreateGroup,
    onEditGroup,
    onRestoreGroup,
    onRefetchReady,
    tooltipResetKey,
    resetTooltips,
    canCreateOptionsAndSupplements,
    canEditOptionsAndSupplements,
    canDeleteRestoreOptionsAndSupplements,
}) => {
    const { t } = useTranslation();
    const { branchId } = useParams<{ branchId: string }>();
    const currentCompanyId = useReactiveVar(currentCompanyIdVar);
    const { primaryLanguage } = useMenuLanguagesData(Number(currentCompanyId));
    const primaryLanguageIso = primaryLanguage?.isoCode?.trim() || null;
    const isTranslationMode = Boolean(versionLocale && primaryLanguageIso && versionLocale !== primaryLanguageIso);

    const [isErrorModalOpen, setIsErrorModalOpen] = useState<number | null>(null);
    const [currentPage, setCurrentPage] = useState(1);
    const [processingId, setProcessingId] = useState<number | null>(null);
    const [deleteModalOpen, setDeleteModalOpen] = useState(false);
    const [groupToDelete, setGroupToDelete] = useState<PartialDishSupplement | null>(null);
    const { showToast, toastText, toggleShowToast, setShowToast } = useToast();
    const canCreateOptionsAndSupplementsByRole = useRoleAccess(CompanyAccess.MenuOptionsAndSupplementsCreate);
    const canEditOptionsAndSupplementsByRole = useRoleAccess(CompanyAccess.MenuOptionsAndSupplementsEdit);
    const canDeleteRestoreOptionsAndSupplementsByRole = useRoleAccess(
        CompanyAccess.MenuOptionsAndSupplementsDeleteRestore
    );
    const canCreate = canCreateOptionsAndSupplements ?? canCreateOptionsAndSupplementsByRole;
    const canEdit = canEditOptionsAndSupplements ?? canEditOptionsAndSupplementsByRole;
    const canDeleteRestore = canDeleteRestoreOptionsAndSupplements ?? canDeleteRestoreOptionsAndSupplementsByRole;
    useScrollToTopOnChange(currentPage);

    const openDeleteModal = (group: PartialDishSupplement) => {
        if (!canDeleteRestore) return;

        setGroupToDelete(group);
        setDeleteModalOpen(true);
    };

    const closeDeleteModal = () => {
        resetTooltips?.();
        setDeleteModalOpen(false);
        setGroupToDelete(null);
    };

    const isFilterNotEmpty = Object.values(filters).some((value) => value !== "" && value !== null);

    const [updateMenuSupplement] = useUpdateMenuSupplementMutation();
    const [deleteMenuSupplement] = useDeleteMenuSupplementMutation();

    const {
        data: dishSupplementsData,
        loading: dishSupplementsLoading,
        refetch: dishSupplementsRefetch,
    } = useGetDishSupplementsQuery({
        variables: {
            companyId: Number(currentCompanyId),
            page: currentPage,
            itemsPerPage: ITEMS_PER_PAGE,
            search: filters?.search ? filters.search : null,
            locale: versionLocale,
        },
        skip: !currentCompanyId || !versionLocale,
        context: { errorType: "global" },
        onError: (error: ApolloError) => {
            setIsErrorModalOpen(ErrorService.errorHandling(error));
        },
        fetchPolicy: "network-only",
    });

    useEffect(() => {
        if (onRefetchReady) {
            onRefetchReady(() => dishSupplementsRefetch);
        }
    }, [onRefetchReady, dishSupplementsRefetch]);

    useEffect(() => {
        setCurrentPage(1);
    }, [filters?.search, versionLocale]);

    const supplementsPaginated = dishSupplementsData?.user?.company?.menuSupplements;
    const supplements: PartialDishSupplement[] = supplementsPaginated?.supplements ?? [];
    const pageCount = parseInt(supplementsPaginated?.pageCount || "0", 10);
    const totalItems = supplementsPaginated?.totalCount || 0;

    const firstItemIndex = (currentPage - 1) * ITEMS_PER_PAGE + 1;
    const lastItemIndex = Math.min(currentPage * ITEMS_PER_PAGE, totalItems);

    const updateGroupSupplement = async (
        group: PartialDishSupplement,
        overrides: GroupOverrides,
        successMessageKey: string,
        canUpdateGroup: boolean
    ) => {
        if (!canUpdateGroup) return;
        if (!currentCompanyId) return;

        const input = makeInputForUpdateGroup(group, overrides);

        try {
            setProcessingId(group.id);
            await updateMenuSupplement({
                variables: {
                    companyId: Number(currentCompanyId),
                    branchId: Number(branchId),
                    dishId: group.id,
                    input,
                    locale: versionLocale,
                },
                context: {
                    errorType: "local",
                },
                onError: (error: ApolloError) => {
                    setIsErrorModalOpen(ErrorService.errorHandling(error));
                },
            });

            await dishSupplementsRefetch();
            toggleShowToast(t(successMessageKey));
        } finally {
            setProcessingId(null);
        }
    };

    const handleToggleRequireOnOrder = async (group: PartialDishSupplement) => {
        if (!canEdit) return;
        if (!currentCompanyId) return;

        const nextValue = !group.isRequireOnOrder;

        await updateGroupSupplement(group, { isRequireOnOrder: nextValue }, "Saved", canEdit);
    };

    const handleArchiveGroup = async () => {
        if (!canDeleteRestore) return;
        if (!currentCompanyId || !groupToDelete) return;

        try {
            setProcessingId(groupToDelete.id);
            await deleteMenuSupplement({
                variables: {
                    branchId: Number(branchId),
                    id: groupToDelete.id,
                },
                context: {
                    errorType: "local",
                },
                onError: (error: ApolloError) => {
                    setIsErrorModalOpen(ErrorService.errorHandling(error));
                },
            });

            await dishSupplementsRefetch();
            toggleShowToast(t("Deleted"));
        } finally {
            setProcessingId(null);
        }

        closeDeleteModal();
    };

    if (dishSupplementsLoading && !dishSupplementsData) return <Spinner />;

    return (
        <>
            <div className="table-responsive mb-4 scrollbar">
                {!!supplements?.length ? (
                    <Table>
                        <thead>
                            <tr className="fs-7">
                                <th className="text-nowrap ps-4">{t("Group Name")}</th>
                                <th className="table-thead-width">{t("Options")}</th>
                                <th className="table-thead-width">{t("Dishes")}</th>
                                <th className="text-nowrap">{t("Number of Options")}</th>
                                <th className="text-nowrap">
                                    {t("Choice when ordering")}
                                    <OverlayTrigger
                                        overlay={
                                            <Tooltip>
                                                {t(
                                                    "Obligatory or optional selection of additives/options when ordering a dish with this group of additives/options"
                                                )}
                                            </Tooltip>
                                        }
                                    >
                                        <i className="bi bi-info-circle ms-2 fs-7 pt-1"></i>
                                    </OverlayTrigger>
                                </th>
                                <th className="text-nowrap">{t("Selectable options")}</th>
                                <th></th>
                            </tr>
                        </thead>
                        <tbody>
                            {supplements.map((group) => {
                                const isArchive = group?.deleted;
                                const colorForArchive = isArchive ? "text-secondary" : "text-dark";
                                const groupNameVm = buildTranslationTextVm({
                                    translatedValue: group?.groupName,
                                    originValue: group?.groupNameOrigin,
                                    isTranslationMode,
                                });
                                return (
                                    <tr key={group.id}>
                                        <td className={`ps-4 align-middle ${colorForArchive}`}>
                                            <span className={colorForArchive}>{groupNameVm.displayText}</span>
                                            {groupNameVm.isMissingTranslation ? (
                                                <TranslationMissingDot tooltipText={t("Edit the language version")} />
                                            ) : null}
                                        </td>
                                        <td className={`align-middle ${colorForArchive}`}>
                                            <RenderNamesCell
                                                items={group.options}
                                                colorClass={colorForArchive}
                                                getKey={(option) => option.id}
                                                formatName={(option) => option.name}
                                                renderItem={(option) => {
                                                    const optionNameVm = buildTranslationTextVm({
                                                        translatedValue: option?.name,
                                                        originValue: option?.nameOrigin,
                                                        isTranslationMode,
                                                    });

                                                    return (
                                                        <span className="d-inline-flex align-items-center">
                                                            <span className="me-1">{optionNameVm.displayText}</span>
                                                            {optionNameVm.isMissingTranslation ? (
                                                                <TranslationMissingDot
                                                                    tooltipText={t("Edit the language version")}
                                                                />
                                                            ) : null}
                                                        </span>
                                                    );
                                                }}
                                            />
                                        </td>
                                        <td className="align-middle">
                                            <RenderNamesCell
                                                items={group.items}
                                                colorClass={colorForArchive}
                                                getKey={(dish) => dish.id}
                                                formatName={(dish) => cloneNameToDisplay(dish.name, t)}
                                                renderItem={(dish) => {
                                                    const dishNameVm = buildTranslationTextVm({
                                                        translatedValue: dish?.name,
                                                        originValue: dish?.nameOrigin,
                                                        isTranslationMode,
                                                    });

                                                    return (
                                                        <span className="d-inline-flex align-items-center">
                                                            <span className="me-1">
                                                                {cloneNameToDisplay(dishNameVm.displayText, t)}
                                                            </span>
                                                            {dishNameVm.isMissingTranslation ? (
                                                                <TranslationMissingDot
                                                                    tooltipText={t("Edit the language version")}
                                                                />
                                                            ) : null}
                                                        </span>
                                                    );
                                                }}
                                            />
                                        </td>
                                        <td className={`align-middle ${colorForArchive}`}>{group.numberOfOption}</td>
                                        <td className="align-middle">
                                            <Form>
                                                <Form.Check
                                                    type="switch"
                                                    label={group.isRequireOnOrder ? t("Required") : t("Unrequited")}
                                                    id={`mandatory-switch-${group.id}`}
                                                    checked={group.isRequireOnOrder}
                                                    onChange={() => handleToggleRequireOnOrder(group)}
                                                    disabled={processingId === group.id || isArchive || !canEdit}
                                                />
                                            </Form>
                                        </td>
                                        <td className={`align-middle ${colorForArchive}`}>{group.maxCountOnOrder}</td>
                                        <td>
                                            <div
                                                className="d-flex gap-2 justify-content-end align-items-center flex-nowrap"
                                                key={tooltipResetKey}
                                            >
                                                {!isArchive ? (
                                                    <>
                                                        <WithRoleAccess
                                                            permission={CompanyAccess.MenuOptionsAndSupplementsEdit}
                                                        >
                                                            <OverlayTrigger overlay={<Tooltip>{t("Edit")}</Tooltip>}>
                                                                <Button
                                                                    variant="outline-secondary"
                                                                    onClick={() => {
                                                                        if (!canEdit) return;
                                                                        resetTooltips?.();
                                                                        onEditGroup(group);
                                                                    }}
                                                                    className="py-2 text-dark rounded-1"
                                                                >
                                                                    <i className="bi bi-pencil"></i>
                                                                </Button>
                                                            </OverlayTrigger>
                                                        </WithRoleAccess>
                                                        <WithRoleAccess
                                                            permission={
                                                                CompanyAccess.MenuOptionsAndSupplementsDeleteRestore
                                                            }
                                                        >
                                                            <OverlayTrigger overlay={<Tooltip>{t("Delete")}</Tooltip>}>
                                                                <Button
                                                                    variant="outline-secondary"
                                                                    onClick={() => {
                                                                        resetTooltips?.();
                                                                        openDeleteModal(group);
                                                                    }}
                                                                    className="py-2 text-dark rounded-1"
                                                                    disabled={processingId === group.id}
                                                                >
                                                                    <i className="bi bi-trash"></i>
                                                                </Button>
                                                            </OverlayTrigger>
                                                        </WithRoleAccess>
                                                    </>
                                                ) : (
                                                    <BadgeAndButtonForArchive
                                                        onButtonClick={() => {
                                                            if (!canDeleteRestore) return;
                                                            resetTooltips?.();
                                                            onRestoreGroup(group);
                                                        }}
                                                        hideButton={!canDeleteRestore}
                                                    />
                                                )}
                                            </div>
                                        </td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </Table>
                ) : null}
            </div>
            {!!totalItems ? (
                <div className="d-flex justify-content-between align-items-center mt-4 mb-5">
                    <Pagination
                        t={t}
                        pages={Number(pageCount)}
                        currentPage={currentPage}
                        onPageChange={(page) => setCurrentPage(page)}
                        firstItemIndex={firstItemIndex}
                        lastItemIndex={lastItemIndex}
                        totalItems={totalItems}
                    />
                </div>
            ) : (
                <RenderEmptyList
                    isFilterNotEmpty={isFilterNotEmpty}
                    imageSrc={emptyImage}
                    altText="Dishes"
                    emptyFilterTitle="No data found for the given values"
                    emptyListTitle="You don't have any additives yet. Create your first option/additive"
                    buttonName={
                        canCreate ? (
                            <span className="fw-bold text-capitalize">
                                <i className="bi bi-plus-lg fw-bold me-2 w-100"></i>
                                {t("Create Group")}
                            </span>
                        ) : null
                    }
                    onClickButton={() => {
                        if (canCreate) onCreateGroup();
                    }}
                />
            )}
            {groupToDelete && (
                <ConfirmDeleteModal
                    show={deleteModalOpen}
                    handleClose={closeDeleteModal}
                    onConfirm={handleArchiveGroup}
                    groupName={groupToDelete.groupName || ""}
                    groupNameOrigin={groupToDelete.groupNameOrigin || ""}
                    isTranslationMode={isTranslationMode}
                    canDeleteRestoreOptionsAndSupplements={canDeleteRestore}
                />
            )}

            {showToast && <ToastComponent show={showToast} setShow={setShowToast} text={toastText} />}
            {isErrorModalOpen ? (
                <ErrorModalComponent
                    i18n={i18next}
                    onClose={() => {
                        setIsErrorModalOpen(null);
                    }}
                    isOpen={!!isErrorModalOpen}
                    currentError={isErrorModalOpen}
                />
            ) : null}
        </>
    );
};

export default SupplementsForDishesTable;
